import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown, Cpu, Wifi, Zap } from "lucide-react";
import ParticlesCanvas from "@/components/ParticlesCanvas";
import { HERO } from "@/constants/testIds";

export default function Hero() {
  const [typed, setTyped] = useState("");
  const full = "> initiate.build --studio=um-software";

  useEffect(() => {
    let i = 0;
    const t = setInterval(() => {
      i += 1;
      setTyped(full.slice(0, i));
      if (i >= full.length) clearInterval(t);
    }, 45);
    return () => clearInterval(t);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid={HERO.section}
      className="relative min-h-screen w-full overflow-hidden pt-16"
    >
      <div className="absolute inset-0 hud-grid opacity-70" />
      <div className="absolute inset-0 radial-fade" />
      <div className="absolute inset-0">
        <ParticlesCanvas />
      </div>

      {/* corner HUD readouts */}
      <div className="hidden md:flex absolute top-24 left-8 flex-col gap-1 z-10 font-hud text-cyan-400/70">
        <div className="flex items-center gap-2"><Wifi size={12} /> uplink://stable</div>
        <div className="flex items-center gap-2"><Cpu size={12} /> node.count 4096</div>
        <div className="flex items-center gap-2"><Zap size={12} /> render 60fps</div>
      </div>
      <div className="hidden md:flex absolute top-24 right-8 flex-col gap-1 z-10 font-hud text-cyan-400/70 items-end">
        <div>lat -6.2088</div>
        <div>lng 106.8456</div>
        <div>SECT / JAKARTA_ID</div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 pt-24 md:pt-32 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="overline"
        >
          software_dev.studio ~ EST. 2017 ~ JKT_ID
        </motion.div>

        <motion.h1
          data-testid={HERO.title}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-6 font-mono-display text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold uppercase tracking-tighter leading-[0.95] text-slate-100"
        >
          <span className="block">We build</span>
          <span className="block">
            <span className="glitch" data-text="digital">digital</span>{" "}
            <span className="text-neon-cyan">products</span>
          </span>
          <span className="block">
            engineered to <span className="text-neon-fuchsia">scale.</span>
          </span>
        </motion.h1>

        <motion.p
          data-testid={HERO.subtitle}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="mt-8 max-w-2xl text-slate-300 text-base sm:text-lg font-body"
        >
          UM Software is a Jakarta-based software development studio. We build custom applications,
          mobile experiences, and enterprise systems for teams that treat shipping like a boss-fight —
          methodical, ambitious, no continues.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <button
            data-testid={HERO.ctaPrimary}
            onClick={() => scrollTo("contact")}
            className="btn-neon"
          >
            Start_Mission <ArrowRight size={14} />
          </button>
          <button
            data-testid={HERO.ctaSecondary}
            onClick={() => scrollTo("portfolio")}
            className="btn-ghost"
          >
            View_Playbook
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-16 font-mono-display text-xs sm:text-sm text-cyan-400/90"
        >
          <span className="caret">{typed}</span>
        </motion.div>
      </div>

      <button
        data-testid={HERO.scrollIndicator}
        onClick={() => scrollTo("services")}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-cyan-400/70 hover:text-cyan-400 transition-colors z-10"
      >
        <span className="font-hud">scroll</span>
        <ChevronDown size={16} className="animate-bounce" />
      </button>
    </section>
  );
}
